<template>
  <v-card>
    <v-toolbar flat color="white" density="compact">
      <v-spacer/>
      <!--RIGHT-TOP CHIP BUTTONS-->
      <div class="chip-container">
        <!--EXCEL EXPORT-->
        <base-action-button @action="showBaseExport">
          <template #prepend><PhFileXls style="vertical-align: middle; margin-right: 5px;" size="20"/></template>
          {{ $store.getters.translate("export") }}
        </base-action-button>
        <!--CREATE-->
        <base-action-button v-if="page.name !== 'notes' && page.name !== 'reports' && canCreate" @action="$emit('create')">
          <template #prepend><PhPlus style="vertical-align: middle; margin-right: 5px;" size="20"/></template>
          {{ $store.getters.translate("create") }}
        </base-action-button>
        <!--FILTER-->
        <base-action-button @action="showFieldsSettings()">
          <template #prepend><PhSlidersHorizontal style="vertical-align: middle;" size="20"/></template>
        </base-action-button>
      </div>
      <v-menu v-if="page.name === 'projects'">
        <template #activator="{ props }">
          <v-btn elevation="0" icon v-bind="props">
            <PhDotsThreeVertical size="24" style="cursor: pointer;"/>
          </v-btn>
        </template>
        <v-list>
          <v-list-item v-if="getClosedState" @click="setClosedState(false)">
            <template #prepend><PhEye size="24"/></template>
            <v-list-item-title class="sm-normal link ml-2">
              <span>
                {{ $store.getters.translate('show_closed') }}
              </span>
            </v-list-item-title>
          </v-list-item>
          <v-list-item v-else @click="setClosedState(true)">
            <template #prepend><PhEyeSlash size="24"/></template>
            <v-list-item-title class="sm-normal link ml-2">
              <span>
                {{ $store.getters.translate('hide_closed') }}
              </span>
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <div v-else style="margin-right: 16px;"/>
    </v-toolbar>
    <div style="overflow-x: auto; width: 100%;">
      <v-data-table
          :headers="fields"
          :items="getRecords"
          class="elevation-1"
          item-value="id"
          selectable-key="id"
          return-object
          :show-select="page.name !== 'roles'"
          v-model="selected"
          v-bind="footer_props"
          v-model:items-per-page="options.itemsPerPage"
          v-model:page="options.page"
          v-model:sort-by="options.sortBy"
          @update:current-items="current = $event">
        <!--CUSTOM SELECT-ALL WITH DROPDOWN-->
        <template #header.data-table-select>
          <v-chip variant="outlined" class="bulk-chip" rounded="pill" size="large" density="comfortable">
            <v-menu :disabled="selected.length === 0">
              <template #activator="{ props: menuProps, isActive }">
                <div class="chip-content">
                  <v-checkbox
                      :model-value="selected.length > 0 && selected.length === getRecords.length"
                      :indeterminate="selected.length > 0 && selected.length < getRecords.length"
                      @click="toggleSelectAll"
                      color="success"
                      hide-details/>
                  <v-btn v-bind="menuProps" v-if="selected.length" icon class="arrow-btn" elevation="0">
                    <PhCaretUp v-if="isActive" size="20"/>
                    <PhCaretDown v-else size="20"/>
                  </v-btn>
                </div>
              </template>
              <v-list v-if="selected.length > 0 && canDelete">
                <!--DELETE-->
                <v-list-item v-if="canDelete" @click.stop="bulkArchive">
                  <template #prepend><PhTrash size="24"/></template>
                  <v-list-item-title class="sm-normal link ml-2">{{ $store.getters.translate("delete") }} {{ selected.length }} {{ $store.getters.translate(page.name) }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-chip>
        </template>
        <template #body.prepend>
          <tr>
            <td v-if="page.name !== 'roles'"/><!--EMPTY TD FOR CHECKBOXES-->
            <td v-for="(key, index) in Object.keys(response_fields)" :key="index">
              <base-field-in-table-header :advanced_search="advanced_search" :field_name="key" :field_type="response_fields[key]" :page_name="page.name" @showRecords="showRecords" @clearSearch="clearSearch"/>
            </td>
            <td v-if="canEdit || canDelete" style="width: 80px;"/><!--EMPTY TD FOR ACTIONS-->
          </tr>
        </template>
        <template #body="{ items }">
          <tr v-for="item in items" :key="item.id" class="sm-normal text-primary" @click.ctrl="handleCtrlClick(item)">
            <td v-if="page.name !== 'roles'">
              <v-checkbox color="success" density="compact" v-model="selected" :key="item.id" :value="item" @click.shift="handleShiftClick(item)"/>
            </td>
            <td v-for="(key, index) in Object.keys(response_fields)" :key="index" @click.exact="$emit('preview', item)">
              <v-chip v-if="page.name === 'plans' && key === 'status'" :color="statuses[getPlanStatus(item)]" size="small" variant="tonal" class="status-chip">
                <span class="tiny-bold">{{ $store.getters.translate(getPlanStatus(item)) }}</span>
              </v-chip>
              <v-chip v-else-if="page.name === 'tickets' && key === 'status'" :color="statuses[getTicketStatus(item)]" size="small" variant="tonal" class="status-chip">
                <span class="tiny-bold">{{ $store.getters.translate(getTicketStatus(item)) }}</span>
              </v-chip>
              <base-field-in-table v-else :item="item" :field_name="key" :field_type="response_fields[key]" :page_name="page.name"/>
            </td>
            <!--ACTION BUTTONS-->
            <td style="text-align: center; width: 80px;" v-if="canEdit || canDelete">
              <v-menu>
                <template #activator="{ props }">
                  <v-btn elevation="0" icon v-bind="props">
                    <PhDotsThreeVertical size="24" style="cursor: pointer;"/>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item v-if="canEdit" @click="$emit('edit', item)">
                    <template #prepend><PhPencilSimpleLine size="24"/></template>
                    <v-list-item-title class="sm-normal link ml-2">{{ $store.getters.translate('edit') }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="canDelete" @click="deleteRecord(item.id)">
                    <template #prepend><PhTrash size="24"/></template>
                    <v-list-item-title class="sm-normal link ml-2">{{ $store.getters.translate('delete') }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </td>
          </tr>
          <tr v-if="getRecords.length === 0">
            <td :colspan="fields.length + 1" class="text-center">
              <span class="sm-normal link">{{ $store.getters.translate("nothing_found") }}</span>
            </td>
          </tr>
        </template>
      </v-data-table>
    </div>
    <base-export ref="base_export" :page="page" :data="getRecords"/>
    <base-modal-fields ref="base_modal_fields" :selected_model="page.model" @refresh="load()"/>
  </v-card>
</template>

<script>
import { useAbility } from '@casl/vue';
import helpFunctions from "../../plugins/helpFunctions.js";
import BaseExport from "./BaseExport";
import BaseModalFields from "./BaseModalFields";
import BaseActionButton from "./BaseActionButton";
import BaseFieldInTable from "./BaseFieldInTable";
import BaseFieldInTableHeader from "./BaseFieldInTableHeader";
import {
  PhDotsThreeVertical,
  PhPencilSimpleLine,
  PhTrash,
  PhCaretUp,
  PhCaretDown,
  PhCheck,
  PhFileXls,
  PhPlus,
  PhSlidersHorizontal,
  PhEye,
  PhEyeSlash,
} from "@phosphor-icons/vue";

export default {
  components: {
    BaseExport,
    BaseModalFields,
    BaseActionButton,
    BaseFieldInTable,
    BaseFieldInTableHeader,
    PhEyeSlash, PhEye, PhDotsThreeVertical, PhPencilSimpleLine, PhTrash, PhCaretDown, PhCaretUp, PhCheck, PhFileXls, PhPlus, PhSlidersHorizontal,
  },
  props: ["page"],
  setup() {
    const { can } = useAbility()
    return { can }
  },
  data() {
    return {
      selected: [],
      all_records: [],
      records: [],
      options: {},
      footer_props: helpFunctions.footer_props,
      advanced_search: {
        tags: [],
      },
      response_fields: {},
      fields: [],
      current: [],
      statuses: helpFunctions.status_colors,
    };
  },
  created() {
    if(helpFunctions.getLocalStorageValue(this.page.name + "_options")) {
      this.options = helpFunctions.getLocalStorageValue(this.page.name + "_options");
    }
    else {
      this.options = helpFunctions.table_options;
    }
  },
  methods: {
    async load() {
      let response = await helpFunctions.getModelsForTables(this.$toast, this.page.name);
      this.all_records = this.page.name === 'reports' ? response.data.filter(item => item.status) : response.data;

      this.response_fields = helpFunctions.replaceIDwithNameBaseTable(response.fields);

      let add_actions_column = this.canEdit || this.canDelete;
      this.fields = helpFunctions.composeFields(this.response_fields, add_actions_column);
      this.showRecords();
    },
    showRecords() {
      let sorted_array = this.all_records;
      this.records = helpFunctions.showRecords(sorted_array, this.response_fields, this.advanced_search, this.page.name);
    },
    clearSearch(key) {
      this.advanced_search[key] = null;
      this.showRecords();
    },
    handleCtrlClick(item) {
      if(this.selected.find((el) => el.id === item.id)) {
        this.selected = this.selected.filter((el) => el.id !== item.id);
      }
      else {
        this.selected.push(item);
      }
    },
    handleShiftClick(item) {
        if (this.selected.length === 1 || this.selected.length === 2) {
          const IDs = [this.selected[1]?.id ?? item.id, this.selected[0].id];    // @click.shift is triggered before v-model="selected" updates, so we need to form a list of IDs from the item.
          let start = this.current.findIndex(el => el.raw.id === Math.min(...IDs));       // getting the starting element index
          let end = this.current.findIndex(el => el.raw.id === Math.max(...IDs));       //  end element index
          if(start > end) {
            let tmp = end;
            end = start;
            start = tmp;
          }
          for (let i = start; i < end; i++) {
            if (!this.selected.find((el) => el.id === this.current[i].key)) {
              this.selected.push(this.current[i].raw);
            }
          }
        }
    },
    toggleSelectAll() {
      const currentValues = this.current.map(item => item.value);
      const allSelected = currentValues.every(cv =>
          this.selected.some(sel => sel.id === cv.id)
      );

      if (allSelected) {
        this.selected = this.selected.filter(item =>
            !currentValues.some(cv => cv.id === item.id)
        );
      } else {
        const selectedIds = new Set(this.selected.map(item => item.id));
        const toAdd = currentValues.filter(cv => !selectedIds.has(cv.id));
        this.selected = [...this.selected, ...toAdd];
      }
    },
    async bulkArchive() {
      this.$confirm(this.$store.getters.translate("delete_confirmation")).then(async (res) => {
        if (res) {
          await helpFunctions.postCustomRoute(this.$toast, "bulk/remove", { model: this.page.model, ids: this.$lodash.map(this.selected, "id") }, true, true);
          this.selected = [];
          await this.load();
        }
      });
    },
    async deleteRecord(id) {
      this.$confirm(this.$store.getters.translate("delete_confirmation")).then(async (res) => {
        if (res) {
          await helpFunctions.deleteModel(this.$toast, this.page.name, id);
          await this.load();
        }
      });
    },
    showBaseExport() {
      this.$refs.base_export.load();
    },
    showFieldsSettings() {
      this.$refs.base_modal_fields.load();
    },
    getPlanStatus(plan) {
      return helpFunctions.planStatus(plan);
    },
    getTicketStatus(ticket) {
      return helpFunctions.ticketStatus(ticket);
    },
    setClosedState(new_state) {
      if(this.page.name === 'projects') {
        this.options.hide_closed_projects = new_state;
      }
    },
  },
  computed: {
    getClosedState() {
      if(this.page.name === 'projects') {
        return this.options.hide_closed_projects;
      }
    },
    getRecords() {
      if(this.page.name === 'projects' && this.options.hide_closed_projects) {
        return this.records.filter((record) => !record.closed);
      }
      else if(this.records) {
        return this.records;
      }
      else {
        return [];
      }
    },
    canCreate() {
      return this.can("create", this.$createEntity(this.page.model.toLowerCase(), { id: 0 }));
    },
    canEdit() {
      return this.can("edit", this.$createEntity(this.page.model.toLowerCase(), { id: 0 }));
    },
    canDelete() {
      return this.can("delete", this.$createEntity(this.page.model.toLowerCase(), { id: 0 }));
    },
  },
  watch: {
    options: {
      handler() {
        helpFunctions.setLocalStorageValue(this.page.name + "_options", this.options);
        if (this.all_records && this.all_records.length > 0) {
          this.showRecords();
        } else {
          this.load();
        }
      },
      deep: true,
    },
    advanced_search: {
      handler() {
        helpFunctions.setLocalStorageValue(this.page.name + "_advanced_search", this.advanced_search);
        this.options.page = 1;
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.v-checkbox {
  display: flex;
}

.bulk-chip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-color: rgba(var(--v-theme-primary), 0.3);
  padding: 0px !important;
}

.chip-content {
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
}

.arrow-btn {
  width: 36px;
  height: 36px;
  min-width: 0;
  background-color: transparent;
}

.chip-container {
  display: flex;
  gap: 8px;
}

.v-text-field >>> input {
  font-size: 12px !important;
}
.v-text-field >>> label {
  font-size: 12px !important;
}

.v-card {
  position: static !important;
  overflow: visible !important;
}
</style>